import { FormEvent, useState } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { Trans, t } from "@lingui/macro";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { safeRedirectPath } from "@/components/me/mePortal";

export type SignInStep = "email" | "password" | "reset";

/** Clerk's first factor for the self-service "forgot password" flow. */
const RESET_STRATEGY = "reset_password_email_code";

/**
 * Our own translated text for the Clerk error codes a user can fix: the
 * password rules and the reset code. Clerk's own text is English only and does
 * not say what a valid password looks like.
 */
function translatedClerkError(
  code: string | undefined,
  step: SignInStep,
): string | undefined {
  // On the reset step the password is a new one, so it cannot be "incorrect".
  const isNewPassword = step === "reset";
  switch (code) {
    case "form_password_pwned":
      return t({
        id: "auth.password.error.pwned",
        message:
          "This password is on a public list of leaked passwords, so others could guess it. Choose a different one, for example 3 or 4 unrelated words.",
      });
    case "form_password_length_too_short":
      return t({
        id: "auth.password.error.tooShort",
        message: "The password is too short. Use at least 8 characters.",
      });
    case "form_password_size_in_bytes_exceeded":
      return t({
        id: "auth.password.error.tooLong",
        message: "The password is too long. Choose a shorter one.",
      });
    // Clerk documents form_password_validation_failed as "Incorrect password"
    // when it rejects a sign-in password.
    case "form_password_incorrect":
    case "form_password_validation_failed":
      if (isNewPassword) return notStrongEnough();
      return t({
        id: "auth.signIn.error.passwordIncorrect",
        message: "The password is not correct. Check it and try again.",
      });
    case "form_password_not_strong_enough":
      return notStrongEnough();
    case "form_code_incorrect":
      return t({
        id: "auth.signIn.error.codeIncorrect",
        message: "The code is not correct. Check the e-mail and try again.",
      });
    case "verification_expired":
    case "verification_failed":
      return t({
        id: "auth.signIn.error.codeExpired",
        message: "This code is no longer valid. Request a new code.",
      });
    default:
      return undefined;
  }
}

function notStrongEnough(): string {
  return t({
    id: "auth.password.error.notStrongEnough",
    message: "This password is too easy to guess. Use at least 8 characters, for example 3 or 4 unrelated words.",
  });
}

function extractClerkError(err: unknown, step: SignInStep): string {
  if (err && typeof err === "object" && "errors" in err) {
    const errors = (err as {
      errors?: Array<{ code?: string; longMessage?: string; message?: string }>;
    }).errors;
    const first = errors?.[0];
    if (first) {
      return (
        translatedClerkError(first.code, step) ?? first.longMessage ?? first.message ?? "Sign-in failed"
      );
    }
  }
  return "Sign-in failed";
}

/**
 * Clerk only offers the reset factor when the instance has password reset via
 * email code enabled AND the identifier resolves to a verified email address.
 * Reading it off the sign-in attempt keeps the link out of the UI on instances
 * where clicking it could only ever fail.
 */
function supportsPasswordReset(attempt: unknown): boolean {
  if (!attempt || typeof attempt !== "object") return false;
  const factors = (attempt as { supportedFirstFactors?: unknown })
    .supportedFirstFactors;
  if (!Array.isArray(factors)) return false;
  return factors.some(
    (factor) =>
      factor &&
      typeof factor === "object" &&
      (factor as { strategy?: string }).strategy === RESET_STRATEGY,
  );
}

interface CustomSignInFormProps {
  step: SignInStep;
  email: string;
  onEmailChange: (email: string) => void;
  onStepChange: (step: SignInStep) => void;
}

export function CustomSignInForm({
  step,
  email,
  onEmailChange,
  onStepChange,
}: CustomSignInFormProps) {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();
  // A signed-out deep link arrives as ?redirect_url=; only a same-origin
  // relative path is honoured (open-redirect protection).
  const [searchParams] = useSearchParams();
  const redirectTarget = safeRedirectPath(searchParams.get("redirect_url")) ?? "/";
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetAvailable, setResetAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setError(null);
    setSubmitting(true);
    try {
      const attempt = await signIn.create({ identifier: email });
      setResetAvailable(supportsPasswordReset(attempt));
      onStepChange("password");
    } catch (err) {
      setError(extractClerkError(err, step));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn || !setActive) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "password",
        password,
      });
      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        navigate(redirectTarget, { replace: true });
      } else {
        setError(
          t({
            id: "auth.signIn.unsupportedState",
            message:
              "Additional verification required. Please contact support.",
          }),
        );
      }
    } catch (err) {
      setError(extractClerkError(err, step));
    } finally {
      setSubmitting(false);
    }
  };

  // Starts a fresh sign-in attempt with the reset factor, which makes Clerk
  // email the one-time code.
  const handleForgotPassword = async () => {
    if (!isLoaded || !signIn) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn.create({ strategy: RESET_STRATEGY, identifier: email });
      setResetCode("");
      setNewPassword("");
      onStepChange("reset");
    } catch (err) {
      setError(extractClerkError(err, step));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn || !setActive) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: RESET_STRATEGY,
        code: resetCode,
        password: newPassword,
      });
      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        navigate(redirectTarget, { replace: true });
      } else {
        setError(
          t({
            id: "auth.signIn.unsupportedState",
            message:
              "Additional verification required. Please contact support.",
          }),
        );
      }
    } catch (err) {
      setError(extractClerkError(err, step));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    onStepChange("email");
    setPassword("");
    setResetCode("");
    setNewPassword("");
    setError(null);
  };

  const handleBackToPassword = () => {
    onStepChange("password");
    setResetCode("");
    setNewPassword("");
    setError(null);
  };

  const errorMessage = error ? (
    <p className="text-sm text-destructive" role="alert">
      {error}
    </p>
  ) : null;

  if (step === "email") {
    return (
      <form
        onSubmit={handleEmailSubmit}
        className="w-full flex flex-col gap-4 flex-1"
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="signin-email">
            <Trans id="auth.signIn.emailLabel">Email address</Trans>
          </Label>
          <Input
            id="signin-email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder={t({
              id: "auth.signIn.emailPlaceholder",
              message: "you@example.com",
            })}
            disabled={submitting}
          />
        </div>
        {errorMessage}
        <Button
          type="submit"
          className="w-full mt-auto"
          size="lg"
          disabled={submitting || !email}
        >
          <Trans id="auth.signIn.continue">Continue</Trans>
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </form>
    );
  }

  if (step === "reset") {
    return (
      <form
        onSubmit={handleResetSubmit}
        className="w-full flex flex-col gap-4 flex-1"
      >
        <p className="text-sm text-muted-foreground">
          <Trans id="auth.signIn.resetHint">
            Enter the code we emailed you and choose a new password.
          </Trans>
        </p>
        <div className="flex flex-col gap-2">
          <Label htmlFor="signin-reset-code">
            <Trans id="auth.signIn.resetCodeLabel">Verification code</Trans>
          </Label>
          <Input
            id="signin-reset-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="signin-new-password">
            <Trans id="auth.signIn.newPasswordLabel">New password</Trans>
          </Label>
          <Input
            id="signin-new-password"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
            aria-describedby="signin-new-password-rules"
          />
          {/* Clerk's rules for this instance: 8+ characters, not in a breach list. */}
          <p id="signin-new-password-rules" className="text-sm text-muted-foreground">
            <Trans id="auth.password.rulesHint">
              At least 8 characters. It must not appear in a known data leak. 3 or 4 unrelated words work well.
            </Trans>
          </p>
        </div>
        {errorMessage}
        <div className="mt-auto flex flex-col gap-2">
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={submitting || !resetCode || !newPassword}
          >
            <Trans id="auth.signIn.resetSubmit">Reset password</Trans>
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleBackToPassword}
            disabled={submitting}
            className="w-full"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <Trans id="common.back">Back</Trans>
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={handlePasswordSubmit}
      className="w-full flex flex-col gap-4 flex-1"
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="signin-password">
          <Trans id="auth.signIn.passwordLabel">Password</Trans>
        </Label>
        <Input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
        />
        {resetAvailable && (
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={submitting}
            className="self-start text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50"
          >
            <Trans id="auth.signIn.forgotPassword">Forgot password?</Trans>
          </button>
        )}
      </div>
      {errorMessage}
      <div className="mt-auto flex flex-col gap-2">
        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={submitting || !password}
        >
          <Trans id="auth.signIn.continue">Continue</Trans>
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          disabled={submitting}
          className="w-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          <Trans id="auth.signIn.useDifferentEmail">
            Use a different email
          </Trans>
        </Button>
      </div>
    </form>
  );
}
